import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useBreakdownTabContext } from '../../contexts/BreakdownTabContext';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { useFilmContext } from '../../contexts/FilmContext';
import {
  getPermissionsByResource,
  GranteeType,
  PermissionRole,
  ResourceType,
  UserPermissions,
} from '../../api/userPermissions';
import { getCurrentUserID } from '../../utils/userUtils';
import { resolveEffectiveElementId } from '../../utils/resolveUserLastUsed';
import {
  ElementType,
  ElementInfo,
  ElementWithUserNames,
  CreateElementParams,
  getRestrictedDatesAsDateStrings,
  ElementRestrictedDateEntry,
} from '../../types/element.types';
import ElementSearch from '../../components/ElementSearch/ElementSearch';
import { useElementUpdates } from '../../hooks/dataUpdates/useElementUpdates';
import { useDataCache } from '../../contexts/BreakdownDataCacheContext';
import { sortElements, sortCastItemsByNO } from '../../utils/elementSortUtils';
import ElementSceneList from './ElementSceneList/ElementSceneList';
import EditScene, { EDIT_SCENE_PANEL_DRAWER_TOP_PX } from '../Schedule/Components/EditScene';
// 导入 SVG 图标组件
import { ReactComponent as CastItemSvgIcon } from '../../assets/svg/Breakdown/Elements/cast.svg';
import { ReactComponent as FeaturedRoleIcon } from '../../assets/svg/Breakdown/Elements/featured.svg';
import { ReactComponent as ExtrasStandInActorIcon } from '../../assets/svg/Breakdown/Elements/background.svg';
import { ReactComponent as ActionActorIcon } from '../../assets/svg/Breakdown/Elements/stunt_players.svg';
import { ReactComponent as AnimalIcon } from '../../assets/svg/Breakdown/Elements/animals.svg';
import { ReactComponent as VehicleIcon } from '../../assets/svg/Breakdown/Elements/vehicles.svg';
import { ReactComponent as ArtPropIcon } from '../../assets/svg/Breakdown/Elements/art_props.svg';
import { ReactComponent as CostumeIcon } from '../../assets/svg/Breakdown/Elements/wardrobe.svg';
import { ReactComponent as MakeupIcon } from '../../assets/svg/Breakdown/Elements/hair_makeup.svg';
import { ReactComponent as SfxMuIcon } from '../../assets/svg/Breakdown/Elements/sfx_mu.svg';
import { ReactComponent as CameraIcon } from '../../assets/svg/Breakdown/Elements/camera.svg';
import { ReactComponent as LightingIcon } from '../../assets/svg/Breakdown/Elements/lighting.svg';
import { ReactComponent as PracticalEffectsIcon } from '../../assets/svg/Breakdown/Elements/sfx.svg';
import { ReactComponent as VfxIcon } from '../../assets/svg/Breakdown/Elements/vfx.svg';
import { ReactComponent as SoundIcon } from '../../assets/svg/Breakdown/Elements/sound.svg';
import { ReactComponent as ExternalStaffIcon } from '../../assets/svg/Breakdown/Elements/addl_labor.svg';
import { ReactComponent as SpecialEquipmentIcon } from '../../assets/svg/Breakdown/Elements/special_equipment.svg';
import { ReactComponent as OtherIcon } from '../../assets/svg/Breakdown/Elements/misc.svg';
import { ReactComponent as NoteIcon } from '../../assets/svg/Breakdown/Elements/note.svg';
import { ReactComponent as ReplaceIcon } from '../../assets/svg/Elements/change.svg';
import { ReactComponent as CopyIcon } from '../../assets/svg/Elements/copy.svg';
import { ReactComponent as DeleteIcon } from '../../assets/svg/Elements/delete.svg';
import { ReactComponent as EditIcon } from '../../assets/svg/Elements/edit.svg';
import { ReactComponent as TextIcon } from '../../assets/svg/Elements/text.svg';
import { ReactComponent as CloseIcon } from '../../assets/svg/Elements/close.svg';
import { ReactComponent as AddIcon } from '../../assets/svg/Elements/add.svg';
import { ReactComponent as BatchEditIcon } from '../../assets/svg/Elements/batch-edit.svg';
import DateSelect from '../../components/DateSelect/DateSelect';
import DropdownMenu, { DropdownMenuItem } from '../../components/DropdownMenu/DropdownMenu';
import { getAllElementsInBreakdownCache } from '../../components/ElementSearch/getAllElementsInBreakdownCache';
import { SlotType, ScheduleInfo } from '../../types/schedule.types';
import { useElementRestrictedDatesOverlap } from '../../hooks/useElementRestrictedDatesOverlap';
import ButtonDialog from '../../components/ButtonDialog';
import './ElementManager.css';

const DATE_FORMAT_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** 模块级映射，便于静态分析识别各图标 import 的使用处 */
const ELEMENT_TYPE_SVG_ICONS: Record<ElementType, React.FC<React.SVGProps<SVGSVGElement>>> = {
  [ElementType.CAST]: CastItemSvgIcon,
  [ElementType.FEATURED]: FeaturedRoleIcon,
  [ElementType.BACKGROUND]: ExtrasStandInActorIcon,
  [ElementType.STUNT_PLAYERS]: ActionActorIcon,
  [ElementType.ANIMALS]: AnimalIcon,
  [ElementType.VEHICLES]: VehicleIcon,
  [ElementType.ART_PROPS]: ArtPropIcon,
  [ElementType.WARDROBE]: CostumeIcon,
  [ElementType.HAIR_MAKEUP]: MakeupIcon,
  [ElementType.SFX_MU]: SfxMuIcon,
  [ElementType.CAMERA]: CameraIcon,
  [ElementType.LIGHTING]: LightingIcon,
  [ElementType.SFX]: PracticalEffectsIcon,
  [ElementType.VFX]: VfxIcon,
  [ElementType.SOUND]: SoundIcon,
  [ElementType.ADDL_LABOR]: ExternalStaffIcon,
  [ElementType.SPECIAL_EQUIPMENT]: SpecialEquipmentIcon,
  [ElementType.MISC]: OtherIcon,
  [ElementType.NOTE]: NoteIcon,
};

/** 有最大数量字段的分类 */
const ELEMENT_TYPES_WITH_MAX_QTY: ElementType[] = [
  ElementType.FEATURED,
  ElementType.BACKGROUND,
  ElementType.STUNT_PLAYERS,
  ElementType.ANIMALS,
  ElementType.VEHICLES,
];

interface ElementManagerProps {
  breakdownId?: string; // 可选，如果提供则使用，否则从 URL params 获取
  currentScheduleID?: string; // 当前拍摄计划 scheduleID（用于查询 slot 等）
  scheduleInfo?: ScheduleInfo | null; // 拍摄计划详情（由父组件从 API 加载，刷新后可直接使用，避免缓存为空时占位渲染）
}

const ElementManager: React.FC<ElementManagerProps> = ({
  breakdownId: breakdownIdProp,
  currentScheduleID,
  scheduleInfo: scheduleInfoProp,
}) => {
  const { t } = useTranslation();
  const { serverSyncedData, setServerSyncedField } = useCurrentUser();
  const { reloadCurrentFilmChildren } = useFilmContext();
  const homeBreakdownCardRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const scheduleHomeBreakdownMetadataRefresh = useCallback(() => {
    if (homeBreakdownCardRefreshTimerRef.current !== undefined) {
      clearTimeout(homeBreakdownCardRefreshTimerRef.current);
    }
    homeBreakdownCardRefreshTimerRef.current = setTimeout(() => {
      homeBreakdownCardRefreshTimerRef.current = undefined;
      void reloadCurrentFilmChildren();
    }, 600);
  }, [reloadCurrentFilmChildren]);
  const { breakdownId: breakdownIdFromParams } = useParams<{ breakdownId: string }>();
  const { breakdownId: contextBreakdownId } = useBreakdownTabContext();
  const breakdownId = breakdownIdProp || contextBreakdownId || breakdownIdFromParams;

  // 与 BreakdownContainer / ScheduleDataCache.currentScheduleId 一致（由父组件传入）
  const effectiveScheduleID = currentScheduleID;
  
  // 标签列表（按需求顺序）
  const tabs: ElementType[] = useMemo(() => [
    ElementType.CAST,
    ElementType.FEATURED,
    ElementType.BACKGROUND,
    ElementType.STUNT_PLAYERS,
    ElementType.ANIMALS,
    ElementType.VEHICLES,
    ElementType.ART_PROPS,
    ElementType.WARDROBE,
    ElementType.HAIR_MAKEUP,
    ElementType.SFX_MU,
    ElementType.CAMERA,
    ElementType.LIGHTING,
    ElementType.SFX,
    ElementType.VFX,
    ElementType.SOUND,
    ElementType.ADDL_LABOR,
    ElementType.SPECIAL_EQUIPMENT,
    ElementType.MISC,
    ElementType.NOTE
  ], []);

  // 当前选中的标签
  const [activeTab, setActiveTab] = useState<ElementType>(ElementType.CAST);
  
  // 选中的 element
  const [selectedElement, setSelectedElement] = useState<ElementInfo | null>(null);
  /** 进入新分场时只应用一次 userLastUsedElementID 恢复分类+选中，避免缓存刷新反复切 tab */
  const lastElementPreferenceBreakdownIdRef = useRef<string | undefined>(undefined);

  // 字段输入本地状态
  const [localInputValues, setLocalInputValues] = useState<Map<string, string>>(new Map());
  
  // 字段编辑状态
  const [, setEditingField] = useState<string | null>(null);
  
  // 头部元素名称编辑状态
  const [isEditingHeaderName, setIsEditingHeaderName] = useState(false);
  const [localHeaderNameValue, setLocalHeaderNameValue] = useState('');
  
  // 字段引用（预留，用于字段聚焦等）
  const _fieldRefs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({});
  void _fieldRefs;
  const headerNameInputRef = useRef<HTMLInputElement | null>(null);
  const changeCategoryButtonRef = useRef<HTMLButtonElement | null>(null);
  const deleteButtonRef = useRef<HTMLButtonElement | null>(null);

  // 更换分类下拉菜单状态
  const [isChangeCategoryMenuOpen, setIsChangeCategoryMenuOpen] = useState(false);
  const [changeCategoryMenuPosition, setChangeCategoryMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // 受限日期编辑弹窗状态
  const [isRestrictedDatesEditorOpen, setIsRestrictedDatesEditorOpen] = useState(false);
  const [restrictedDatesEditorPosition, setRestrictedDatesEditorPosition] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const [restrictedDatesEditorValue, setRestrictedDatesEditorValue] = useState('');
  const [isRestrictedDatesBatchEditOpen, setIsRestrictedDatesBatchEditOpen] = useState(false);
  const restrictedDatesBatchEditSnapshotRef = useRef<ElementRestrictedDateEntry[]>([]);
  const restrictedDatesEditButtonRef = useRef<HTMLDivElement | null>(null);
  const restrictedDatesEditorRef = useRef<HTMLDivElement | null>(null);
  const restrictedDatesAddButtonRef = useRef<HTMLButtonElement | null>(null);
  const restrictedDatesEditorListRef = useRef<ElementRestrictedDateEntry[]>([]);
  const [isDateSelectVisible, setIsDateSelectVisible] = useState(false);
  const [dateSelectPosition, setDateSelectPosition] = useState({ top: 0, left: 0 });

  // 删除元素确认弹窗状态
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
  // 从右侧打开的场次详情抽屉（关联场次表格中点击编辑图标）
  const [editingSceneId, setEditingSceneId] = useState<string | null>(null);
  // 右侧日历区：是否显示文本视图（否则显示日历视图）
  const [showCalendarTextView, setShowCalendarTextView] = useState(false);
  // 文本视图「复制以下信息」点击后显示「已复制」，1s 后恢复
  const [calendarTextJustCopied, setCalendarTextJustCopied] = useState(false);

  // 更换分类下拉菜单项（图标 + 类型名称）
  const changeCategoryDropdownItems: DropdownMenuItem[] = useMemo(() =>
    tabs.map((tab) => ({
      id: tab,
      label: String(t(`sceneDetails.itemTabs.${tab}`)),
      icon: React.createElement(ELEMENT_TYPE_SVG_ICONS[tab], {
        className: 'element-manager-tab-icon',
        style: { width: 14, height: 14 },
      }),
    })),
    [tabs, t]
  );

  // 点击更换分类按钮
  const handleChangeCategoryClick = useCallback(() => {
    if (!changeCategoryButtonRef.current) return;
    const rect = changeCategoryButtonRef.current.getBoundingClientRect();
    setChangeCategoryMenuPosition({ top: rect.bottom + 4, left: rect.left });
    setIsChangeCategoryMenuOpen(true);
  }, []);

  // 点击受限日期编辑图标（弹窗右边框与编辑按钮右边框对齐）
  const RESTRICTED_DATES_EDITOR_WIDTH = 400;
  // 受限日期编辑弹窗内列表（[date, note][]），与 textarea 同步
  const [restrictedDatesEditorList, setRestrictedDatesEditorList] = useState<
    ElementRestrictedDateEntry[]
  >([]);
  // 当前正在编辑备注的日期（该行的备注可编辑）
  const [editingNoteDate, setEditingNoteDate] = useState<string | null>(null);
  const handleRestrictedDatesEditClick = useCallback(() => {
    if (!restrictedDatesEditButtonRef.current || !selectedElement) return;
    const rect = restrictedDatesEditButtonRef.current.getBoundingClientRect();
    setRestrictedDatesEditorPosition({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
    const list = selectedElement.elementRestrictedDates ?? [];
    setRestrictedDatesEditorList(list);
    setRestrictedDatesEditorValue(list.map(([d]) => d).join('\n'));
    setEditingNoteDate(null);
    setIsRestrictedDatesBatchEditOpen(false);
    setIsRestrictedDatesEditorOpen(true);
  }, [selectedElement]);

  // 更新列表中某日期的备注
  const setRestrictedDatesEditorNote = useCallback((date: string, note: string) => {
    setRestrictedDatesEditorList((prev) =>
      prev.map(([d, n]) => (d === date ? [d, note] : [d, n]))
    );
  }, []);

  // 使用数据缓存和更新 hooks
  const {
    getBreakdownCache,
    subscribeBreakdownCache,
    getElementCache,
    subscribeElementCache,
    getElementRelationCache,
    subscribeElementRelationCache,
    getSlotCache,
    getScheduleCache,
  } = useDataCache();
  const [breakdownPermissions, setBreakdownPermissions] = useState<UserPermissions[]>([]);
  const [breakdownOwnerID, setBreakdownOwnerID] = useState<string | null>(null);
  const [relatedScenesCacheTick, setRelatedScenesCacheTick] = useState(0);

  useEffect(() => {
    if (!breakdownId) return;
    const update = () => setRelatedScenesCacheTick((value) => value + 1);
    const unsubscribeRelations = subscribeElementRelationCache(breakdownId, update);
    const unsubscribeBreakdown = subscribeBreakdownCache(breakdownId, update);
    return () => {
      unsubscribeRelations();
      unsubscribeBreakdown();
    };
  }, [breakdownId, subscribeElementRelationCache, subscribeBreakdownCache]);

  useEffect(() => {
    if (!breakdownId) {
      setBreakdownPermissions([]);
      return;
    }
    let cancelled = false;
    void getPermissionsByResource(ResourceType.BREAKDOWN, breakdownId)
      .then((response) => {
        if (!cancelled) setBreakdownPermissions(response.data || []);
      })
      .catch((error) => {
        if (!cancelled) console.error('加载分场分景权限失败:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [breakdownId]);

  useEffect(() => {
    if (!breakdownId) {
      setBreakdownOwnerID(null);
      return;
    }
    const updateOwner = () => {
      setBreakdownOwnerID(getBreakdownCache(breakdownId)?.breakdownInfo?.breakdownOwnerID || null);
    };
    updateOwner();
    return subscribeBreakdownCache(breakdownId, updateOwner);
  }, [breakdownId, getBreakdownCache, subscribeBreakdownCache]);

  const currentUserID = getCurrentUserID();
  const currentBreakdownRole = breakdownPermissions.find(
    (permission) =>
      permission.granteeType === GranteeType.USER && permission.granteeID === currentUserID
  )?.role;
  const canEditBreakdown = Boolean(
    breakdownOwnerID === currentUserID ||
    currentBreakdownRole === PermissionRole.ADMIN ||
    currentBreakdownRole === PermissionRole.EDITOR
  );

  useEffect(() => {
    if (canEditBreakdown) return;
    setIsEditingHeaderName(false);
    setIsChangeCategoryMenuOpen(false);
    setIsRestrictedDatesEditorOpen(false);
    setIsDeleteDialogVisible(false);
    setEditingSceneId(null);
  }, [canEditBreakdown]);
  const { updateElementData, deleteElementData, createElementData, batchCreateElementRelationsData } =
    useElementUpdates({
      onSuccess: () => scheduleHomeBreakdownMetadataRefresh(),
    });

  /** 复制场次元素：名称用后缀规则去重；简称在末尾加「'」，直至与同类型不重复；场次关联数量逐条复制 */
  const [isDuplicatingElement, setIsDuplicatingElement] = useState(false);
  const duplicateElementInProgressRef = useRef(false);
  const handleDuplicateElement = useCallback(async () => {
    if (!breakdownId || !selectedElement || duplicateElementInProgressRef.current) return;
    const source = selectedElement;
    const cache = getElementCache(breakdownId);
    const relationCache = getElementRelationCache(breakdownId);

    const existingNames = new Set(
      (cache?.elements ?? []).map((i) => (i.elementName ?? '').trim()).filter(Boolean)
    );
    const originalName = (source.elementName ?? '').trim();
    const nameBase = originalName || t('schedule.untitled');
    let suffixNumber = 1;
    let newName = `${nameBase}${t('common.copyNameSuffix')}`;
    while (existingNames.has(newName)) {
      suffixNumber += 1;
      newName = `${nameBase}${t('common.copyNameSuffixNumbered', { n: suffixNumber })}`;
    }

    const sameTypeItems = (cache?.elements ?? []).filter(
      (i) => i.elementType === source.elementType
    );
    const usedAbbrs = new Set(
      sameTypeItems.map((i) => (i.elementAbbr ?? '').trim()).filter((a) => a.length > 0)
    );
    const baseAbbr = (source.elementAbbr ?? '').trim();
    let newAbbr: string | undefined;
    if (baseAbbr) {
      newAbbr = `${baseAbbr}'`;
      while (usedAbbrs.has(newAbbr)) {
        newAbbr = `${newAbbr}'`;
      }
    }

    const relationsFromSource =
      relationCache?.elementRelations.filter((r) => r.elementID === source.elementID) ?? [];
    const maxFromRels =
      relationsFromSource.length > 0
        ? Math.max(...relationsFromSource.map((r) => r.elementQtyInScene ?? 1))
        : 1;
    const initialMax = source.elementMaxQtyInBreakdown != null ? source.elementMaxQtyInBreakdown : maxFromRels;

    const createPayload: CreateElementParams = {
      elementBelongBreakdownID: breakdownId,
      elementName: newName,
      ...(newAbbr !== undefined ? { elementAbbr: newAbbr } : {}),
      elementType: source.elementType,
      elementSceneNumber: source.elementSceneNumber,
      elementQuantity: source.elementQuantity,
      elementCreatorID: source.elementCreatorID,
      elementDataStatus: source.elementDataStatus,
      elementNO: source.elementNO,
      elementRestrictedDates: source.elementRestrictedDates,
      elementMaxQtyInBreakdown: source.elementMaxQtyInBreakdown,
      elementQtyInScene: initialMax,
    };

    duplicateElementInProgressRef.current = true;
    setIsDuplicatingElement(true);
    try {
      const newId = await createElementData(breakdownId, createPayload);
      if (relationsFromSource.length > 0) {
        await batchCreateElementRelationsData(
          breakdownId,
          relationsFromSource.map((r) => ({
            elementID: newId,
            sceneID: r.sceneID,
            elementQtyInScene: r.elementQtyInScene,
            elementBelongBreakdownID: breakdownId,
          }))
        );
      }
      const updatedCache = getElementCache(breakdownId);
      const newItem = updatedCache?.elements.find((i) => i.elementID === newId);
      if (newItem) {
        setSelectedElement(newItem);
        setServerSyncedField('userLastUsedElementID', `${breakdownId};${newId}`);
      }
    } catch (error) {
      console.error('[ElementManager] 复制场次元素失败:', error);
    } finally {
      duplicateElementInProgressRef.current = false;
      setIsDuplicatingElement(false);
    }
  }, [
    breakdownId,
    selectedElement,
    t,
    getElementCache,
    getElementRelationCache,
    createElementData,
    batchCreateElementRelationsData,
    setServerSyncedField,
  ]);

  // 校验是否存在无效格式行（非空且不符合 YYYY-MM-DD）
  const hasInvalidRestrictedDatesLines = useMemo(() => {
    const lines = restrictedDatesEditorValue.split('\n').map((s) => s.trim());
    return lines.some(
      (line) => line !== '' && !DATE_FORMAT_REGEX.test(line)
    );
  }, [restrictedDatesEditorValue]);

  // textarea 变更时同步列表：解析日期行，保留已有备注（仅批量编辑模式下生效）
  useEffect(() => {
    if (!isRestrictedDatesEditorOpen || !isRestrictedDatesBatchEditOpen) return;
    const lines = restrictedDatesEditorValue
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const dates = lines.filter((line) => DATE_FORMAT_REGEX.test(line)).sort();
    setRestrictedDatesEditorList((prev) =>
      dates.map((d) => {
        const found = prev.find(([pd]) => pd === d);
        return [d, found ? found[1] : ''] as ElementRestrictedDateEntry;
      })
    );
  }, [restrictedDatesEditorValue, isRestrictedDatesEditorOpen, isRestrictedDatesBatchEditOpen]);

  // 同步当前受限日期列表到服务端（用于关闭时保存备注、单日添加后、批量保存后）
  const syncRestrictedDatesToList = useCallback(async (list: ElementRestrictedDateEntry[]) => {
    if (!selectedElement || !breakdownId) return;
    const next = list
      .map(([d, n]) => [d, n] as [string, string])
      .sort((a, b) => a[0].localeCompare(b[0]));
    try {
      await updateElementData(breakdownId, selectedElement.elementID, {
        elementRestrictedDates: next,
      });
      setSelectedElement((prev) =>
        prev ? { ...prev, elementRestrictedDates: next.length > 0 ? next : undefined } : null
      );
    } catch (error) {
      console.error('[ElementManager] 更新受限日期失败:', error);
    }
  }, [selectedElement, breakdownId, updateElementData]);

  // 删除列表中的单条受限日期
  const handleRestrictedDateDelete = useCallback(
    async (dateStr: string) => {
      if (!selectedElement || !breakdownId) return;
      const prev = restrictedDatesEditorListRef.current;
      const next = prev.filter(([d]) => d !== dateStr);
      setRestrictedDatesEditorList(next);
      setRestrictedDatesEditorValue(next.map(([d]) => d).join('\n'));
      if (editingNoteDate === dateStr) setEditingNoteDate(null);
      await syncRestrictedDatesToList(next);
    },
    [selectedElement, breakdownId, editingNoteDate, syncRestrictedDatesToList]
  );

  // 单日添加：打开 DateSelect 弹窗
  const handleRestrictedDatesAddClick = useCallback(() => {
    if (restrictedDatesAddButtonRef.current) {
      const rect = restrictedDatesAddButtonRef.current.getBoundingClientRect();
      setDateSelectPosition({ top: rect.bottom + 4, left: rect.left });
      setIsDateSelectVisible(true);
    }
  }, []);

  // 单日添加：选择日期后插入列表并同步服务端
  const handleDateSelectDate = useCallback(
    (date: Date) => {
      if (!selectedElement || !breakdownId) return;
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const prev = restrictedDatesEditorListRef.current;
      if (prev.some(([d]) => d === dateStr)) {
        setIsDateSelectVisible(false);
        return;
      }
      const next: ElementRestrictedDateEntry[] = [...prev, [dateStr, ''] as ElementRestrictedDateEntry].sort(
        (a, b) => a[0].localeCompare(b[0])
      );
      setRestrictedDatesEditorList(next);
      updateElementData(breakdownId, selectedElement.elementID, {
        elementRestrictedDates: next,
      });
      setSelectedElement((p) => (p ? { ...p, elementRestrictedDates: next } : null));
      setIsDateSelectVisible(false);
    },
    [selectedElement, breakdownId, updateElementData]
  );

  // 批量编辑：打开（保存快照）
  const handleRestrictedDatesOpenBatchEdit = useCallback(() => {
    restrictedDatesBatchEditSnapshotRef.current = [...restrictedDatesEditorList];
    setRestrictedDatesEditorValue(restrictedDatesEditorList.map(([d]) => d).join('\n'));
    setIsRestrictedDatesBatchEditOpen(true);
  }, [restrictedDatesEditorList]);

  // 批量编辑：取消（恢复快照，隐藏 textarea）
  const handleRestrictedDatesBatchEditCancel = useCallback(() => {
    const snapshot = restrictedDatesBatchEditSnapshotRef.current;
    setRestrictedDatesEditorList(snapshot);
    setRestrictedDatesEditorValue(snapshot.map(([d]) => d).join('\n'));
    setIsRestrictedDatesBatchEditOpen(false);
  }, []);

  // 批量编辑：保存（解析 textarea，与现有合并去重，仅新增/删除）
  const handleRestrictedDatesBatchEditSave = useCallback(async () => {
    if (!selectedElement || !breakdownId) return;
    if (hasInvalidRestrictedDatesLines) return;
    const lines = restrictedDatesEditorValue
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const parsedDates = lines.filter((line) => DATE_FORMAT_REGEX.test(line)).sort();
    const existingMap = new Map(restrictedDatesEditorList.map(([d, n]) => [d, n]));
    const merged: ElementRestrictedDateEntry[] = parsedDates.map((d) => [
      d,
      existingMap.get(d) ?? '',
    ]);
    const next = merged.sort((a, b) => a[0].localeCompare(b[0]));
    setRestrictedDatesEditorList(next);
    setRestrictedDatesEditorValue(next.map(([d]) => d).join('\n'));
    await syncRestrictedDatesToList(next);
    setSelectedElement((prev) =>
      prev ? { ...prev, elementRestrictedDates: next.length > 0 ? next : undefined } : null
    );
    setIsRestrictedDatesBatchEditOpen(false);
  }, [
    selectedElement,
    breakdownId,
    restrictedDatesEditorValue,
    restrictedDatesEditorList,
    hasInvalidRestrictedDatesLines,
    syncRestrictedDatesToList,
  ]);

  // 保持 ref 与列表同步，供关闭时读取最新数据
  restrictedDatesEditorListRef.current = restrictedDatesEditorList;

  // 关闭受限日期编辑弹窗（仅同步当前列表的备注到服务端，不提交未保存的批量 textarea 内容）
  const handleRestrictedDatesEditorClose = useCallback(async () => {
    await syncRestrictedDatesToList(restrictedDatesEditorListRef.current);
    setIsRestrictedDatesBatchEditOpen(false);
    setIsRestrictedDatesEditorOpen(false);
  }, [syncRestrictedDatesToList]);

  // 点击外部关闭受限日期编辑弹窗（仅同步备注）；点击 DateSelect 内部时不关闭
  useEffect(() => {
    if (!isRestrictedDatesEditorOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        restrictedDatesEditorRef.current?.contains(target) ||
        restrictedDatesEditButtonRef.current?.contains(target) ||
        target.closest('.date-select-container') ||
        target.closest('.date-select-overlay')
      ) {
        return;
      }
      handleRestrictedDatesEditorClose();
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [isRestrictedDatesEditorOpen, handleRestrictedDatesEditorClose]);

  // 选择新分类（含简称、最大数量的联动规则）
  const handleChangeCategorySelect = useCallback(
    async (item: DropdownMenuItem) => {
      if (!selectedElement || !breakdownId) return;
      const newType = item.id as ElementType;
      const oldType = selectedElement.elementType as ElementType | undefined;
      if (newType === oldType) {
        setIsChangeCategoryMenuOpen(false);
        return;
      }

      const updates: Partial<ElementInfo> = { elementType: newType };

      // 1. 主要角色 → 其他类型：清空简称
      if (oldType === ElementType.CAST && newType !== ElementType.CAST) {
        updates.elementAbbr = undefined;
      }

      // 2. 其他类型 → 主要角色：无简称时自动生成（取名称首字，遇重名追加'符号）
      if (newType === ElementType.CAST && !selectedElement.elementAbbr?.trim()) {
        const name = selectedElement.elementName?.trim();
        if (name) {
          const firstChar = name.charAt(0);
          const allItems = await getAllElementsInBreakdownCache(breakdownId, getElementCache);
          const usedAbbrs = new Set(
            allItems
              .filter(
                (i: ElementWithUserNames) =>
                  i.elementType === ElementType.CAST && i.elementID !== selectedElement.elementID
              )
              .map((i: ElementWithUserNames) => (i.elementAbbr || '').trim())
              .filter(Boolean)
          );
          let abbr = firstChar;
          while (usedAbbrs.has(abbr)) {
            abbr += "ⁱ";
          }
          updates.elementAbbr = abbr;
        }
      }

      // 3. 有最大数量类型 → 无最大数量类型：清空最大数量
      const oldHasMaxQty = oldType && ELEMENT_TYPES_WITH_MAX_QTY.includes(oldType);
      const newHasMaxQty = ELEMENT_TYPES_WITH_MAX_QTY.includes(newType);
      if (oldHasMaxQty && !newHasMaxQty) {
        updates.elementMaxQtyInBreakdown = undefined;
      }

      // 4. 无最大数量类型 → 有最大数量类型：默认 1
      if (!oldHasMaxQty && newHasMaxQty) {
        updates.elementMaxQtyInBreakdown = 1;
      }

      try {
        await updateElementData(breakdownId, selectedElement.elementID, updates);
        setSelectedElement((prev) => (prev ? { ...prev, ...updates } : null));
        setActiveTab(newType);
        setServerSyncedField('userLastUsedElementID', `${breakdownId};${selectedElement.elementID}`);
      } catch (error) {
        console.error('[ElementManager] 更换分类失败:', error);
      }
      setIsChangeCategoryMenuOpen(false);
    },
    [selectedElement, breakdownId, updateElementData, getElementCache, setServerSyncedField]
  );
  
  // 处理 element 选择（搜索查看 / 行点击）：写入服务端记忆并同步数据库
  const handleElementSelect = useCallback(
    (element: ElementInfo) => {
      setSelectedElement(element);
      setLocalInputValues(new Map());
      setEditingField(null);
      setIsEditingHeaderName(false);
      if (breakdownId && element.elementID) {
        setServerSyncedField('userLastUsedElementID', `${breakdownId};${element.elementID}`);
      }
    },
    [breakdownId, setServerSyncedField]
  );
  
  // 处理字段更新
  const handleFieldUpdate = useCallback(async (field: string, value: string) => {
    if (!selectedElement || !breakdownId) return;
    
    try {
      const updateData: Partial<ElementInfo> = {};
      
      // 根据字段类型转换值
      if (field === 'elementMaxQtyInBreakdown') {
        const numValue = value === '' ? undefined : parseFloat(value);
        if (value !== '' && (isNaN(numValue as number) || numValue === null)) return; // 无效数字
        (updateData as any)[field] = numValue;
      } else {
        (updateData as any)[field] = value === '' ? undefined : value;
      }
      
      await updateElementData(breakdownId, selectedElement.elementID, updateData);
      
      // 更新本地选中的 element
      setSelectedElement(prev => prev ? { ...prev, ...updateData } : null);
      
      // 清除编辑状态
      setEditingField(null);
    } catch (error) {
      console.error('[ElementManager] 更新字段失败:', error);
    }
  }, [selectedElement, breakdownId, updateElementData]);
  
  // 处理头部元素名称更新
  const handleHeaderNameBlur = useCallback(async () => {
    const trimmed = localHeaderNameValue.trim();
    if (trimmed && selectedElement && breakdownId) {
      await handleFieldUpdate('elementName', trimmed);
      setSelectedElement(prev => prev ? { ...prev, elementName: trimmed } : null);
    }
    setIsEditingHeaderName(false);
    setLocalHeaderNameValue('');
  }, [localHeaderNameValue, selectedElement, breakdownId, handleFieldUpdate]);
  
  const handleHeaderNameKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
    if (e.key === 'Escape') {
      setLocalHeaderNameValue(selectedElement?.elementName || '');
      setIsEditingHeaderName(false);
    }
  }, [selectedElement?.elementName]);
  
  // 进入编辑状态时聚焦输入框
  React.useEffect(() => {
    if (isEditingHeaderName) {
      headerNameInputRef.current?.focus();
    }
  }, [isEditingHeaderName]);
  
  // 创建字段处理函数（预留，用于可编辑字段）
  const _createFieldHandlers = useCallback((field: string) => {
    return {
      onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
        setEditingField(field);
        e.target.classList.add('editing');
      },
      onBlur: async (e: React.FocusEvent<HTMLInputElement>) => {
        const key = field;
        const localValue = localInputValues.get(key);
        const finalValue = localValue !== undefined ? localValue : e.target.value;
        const trimmedValue = finalValue.trim();
        
        // 更新输入框的值（如果使用了本地值）
        if (localValue !== undefined) {
          e.target.value = trimmedValue;
        }
        
        // 提交更新
        await handleFieldUpdate(field, trimmedValue);
        
        // 清除本地状态
        setLocalInputValues(prev => {
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
        
        // 移除编辑状态
        e.target.classList.remove('editing');
        setEditingField(null);
      },
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputVal = e.target.value;
        setLocalInputValues(prev => {
          const next = new Map(prev);
          next.set(field, inputVal);
          return next;
        });
      },
    };
  }, [localInputValues, handleFieldUpdate]);
  void _createFieldHandlers;

  // 监听缓存变化，更新选中的 element（包括受限日期字段）
  React.useEffect(() => {
    if (!selectedElement || !breakdownId) return;
    
    const updateSelectedItem = () => {
      const cache = getElementCache(breakdownId);
      if (cache) {
        const updatedItem = cache.elements.find(item => item.elementID === selectedElement.elementID);
        if (updatedItem) {
          setSelectedElement(updatedItem);
        }
      }
    };

    // 立即执行一次
    updateSelectedItem();

    // 订阅缓存变化
    const unsubscribe = subscribeElementCache(breakdownId, updateSelectedItem);

    return () => {
      unsubscribe();
    };
  }, [selectedElement, breakdownId, getElementCache, subscribeElementCache]);

  // 自动选中元素：进入分场时按 userLastUsedElementID 恢复「分类 tab + 元素 + 详情」；切换分类或缓存变化时维护当前 tab 内选中
  React.useEffect(() => {
    if (!breakdownId) {
      lastElementPreferenceBreakdownIdRef.current = undefined;
      return;
    }

    const updateFirstItem = () => {
      const cache = getElementCache(breakdownId);
      if (!cache || !cache.elements) return;

      const allItems = cache.elements;

      if (allItems.length === 0) {
        setSelectedElement(null);
        setLocalInputValues(new Map());
        setEditingField(null);
        return;
      }

      const preferredId = resolveEffectiveElementId(
        serverSyncedData.userLastUsedElementID,
        breakdownId,
        allItems
      );
      const preferredItem = preferredId
        ? allItems.find((item) => item.elementID === preferredId)
        : null;

      // 新分场首次：在全部分类中解析记忆，切换到对应 tab 并选中（右侧详情同步为该元素）
      if (
        lastElementPreferenceBreakdownIdRef.current !== breakdownId &&
        preferredItem
      ) {
        lastElementPreferenceBreakdownIdRef.current = breakdownId;
        setActiveTab(preferredItem.elementType as ElementType);
        setSelectedElement(preferredItem);
        setLocalInputValues(new Map());
        setEditingField(null);
        return;
      }
      if (lastElementPreferenceBreakdownIdRef.current !== breakdownId) {
        lastElementPreferenceBreakdownIdRef.current = breakdownId;
      }

      const itemsOfCurrentType = cache.elements.filter((item) => item.elementType === activeTab);

      if (itemsOfCurrentType.length === 0) {
        setSelectedElement(null);
        setLocalInputValues(new Map());
        setEditingField(null);
        return;
      }

      const sortedItems =
        activeTab === ElementType.CAST
          ? sortCastItemsByNO(itemsOfCurrentType)
          : sortElements(itemsOfCurrentType);

      const preferredInTab =
        preferredItem && preferredItem.elementType === activeTab ? preferredItem : null;
      const defaultItem =
        (preferredInTab
          ? sortedItems.find((item: ElementInfo) => item.elementID === preferredInTab.elementID)
          : null) || sortedItems[0];

      setSelectedElement((prevSelected) => {
        if (!prevSelected || prevSelected.elementType !== activeTab) {
          setLocalInputValues(new Map());
          setEditingField(null);
          return defaultItem;
        }

        const currentSelectedInList = sortedItems.find(
          (item: ElementInfo) => item.elementID === prevSelected.elementID
        );
        if (!currentSelectedInList) {
          setLocalInputValues(new Map());
          setEditingField(null);
          return defaultItem;
        }

        return prevSelected;
      });
    };

    updateFirstItem();

    const unsubscribe = subscribeElementCache(breakdownId, updateFirstItem);

    return () => {
      unsubscribe();
    };
  }, [breakdownId, activeTab, getElementCache, subscribeElementCache, serverSyncedData.userLastUsedElementID]);

  // 获取拍摄计划信息（用于日历月份范围）：优先使用父组件传入的 scheduleInfo（刷新后直接可用），否则从缓存读取
  const resolvedScheduleInfo = useMemo(() => {
    if (scheduleInfoProp && scheduleInfoProp.scheduleID === effectiveScheduleID) return scheduleInfoProp;
    if (!effectiveScheduleID || !breakdownId) return null;
    const slotCache = getSlotCache(effectiveScheduleID);
    if (slotCache?.scheduleInfo) return slotCache.scheduleInfo;
    const scheduleSlice = getScheduleCache(breakdownId);
    if (scheduleSlice?.currentScheduleId === effectiveScheduleID && scheduleSlice.currentScheduleInfo) {
      return scheduleSlice.currentScheduleInfo;
    }
    return scheduleSlice?.schedules?.find((p) => p.scheduleID === effectiveScheduleID) ?? null;
  }, [scheduleInfoProp, effectiveScheduleID, breakdownId, getSlotCache, getScheduleCache]);

  // 关联场次的 sceneIDs（与 ElementSceneList 相同逻辑）
  const relatedSceneIDs = useMemo(() => {
    if (!breakdownId || !selectedElement) return new Set<string>();
    void relatedScenesCacheTick;
    const relationCache = getElementRelationCache(breakdownId);
    if (!relationCache?.elementRelations) return new Set<string>();
    const relations = relationCache.elementRelations.filter(
      (rel) => rel.elementID === selectedElement.elementID
    );
    return new Set(relations.map((rel) => rel.sceneID));
  }, [breakdownId, selectedElement, relatedScenesCacheTick, getElementRelationCache]);

  const relatedScenesTotalPages = useMemo(() => {
    if (!breakdownId || relatedSceneIDs.size === 0) return 0;
    void relatedScenesCacheTick;
    const scenes = getBreakdownCache(breakdownId)?.scenes ?? [];
    const total = scenes.reduce((sum, scene) => {
      if (!relatedSceneIDs.has(scene.sceneID)) return sum;
      const pages = Number(scene.scenePagesNumber);
      return Number.isFinite(pages) ? sum + pages : sum;
    }, 0);
    return Math.round(total * 10) / 10;
  }, [breakdownId, relatedSceneIDs, relatedScenesCacheTick, getBreakdownCache]);

  // 拍摄日期：slotDate 集合（仅 SHOOT 类型，且 slotBelongSceneID 在 relatedSceneIDs 中）
  const slotDatesSet = useMemo(() => {
    if (!effectiveScheduleID || relatedSceneIDs.size === 0) return new Set<string>();
    const slotCache = getSlotCache(effectiveScheduleID);
    const slots = slotCache?.slots ?? [];
    const dates = new Set<string>();
    for (const pt of slots) {
      if (
        (pt.slotType === SlotType.SHOOT || !pt.slotType) &&
        pt.slotBelongSceneID &&
        relatedSceneIDs.has(pt.slotBelongSceneID) &&
        pt.slotDate
      ) {
        const dateStr = typeof pt.slotDate === 'string'
          ? pt.slotDate.slice(0, 10)
          : pt.slotDate;
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) dates.add(dateStr);
      }
    }
    return dates;
  }, [effectiveScheduleID, relatedSceneIDs, getSlotCache]);

  /** 拍摄日期展示用：与 slotDatesSet 一致的去重任务日期的最早、最晚一天 */
  const slotShootingDateRange = useMemo(() => {
    if (slotDatesSet.size === 0) return null;
    const sorted = Array.from(slotDatesSet).sort();
    return { start: sorted[0], end: sorted[sorted.length - 1] };
  }, [slotDatesSet]);

  // 受限日期与计划日期重叠的 elementID 集合（用于 ElementSearch 名称显示冲突日期色）
  const elementIdsWithRestrictedDatesOverlap = useElementRestrictedDatesOverlap(breakdownId ?? undefined, effectiveScheduleID ?? undefined);

  // 受限日期：elementRestrictedDates 日期集合
  const restrictedDatesSet = useMemo(() => {
    const arr = getRestrictedDatesAsDateStrings(selectedElement?.elementRestrictedDates);
    return new Set(arr);
  }, [selectedElement?.elementRestrictedDates]);

  // 冲突日期：slotDates ∩ restrictedDates
  const conflictDatesSet = useMemo(() => {
    const conflict = new Set<string>();
    slotDatesSet.forEach((d) => {
      if (restrictedDatesSet.has(d)) conflict.add(d);
    });
    return conflict;
  }, [slotDatesSet, restrictedDatesSet]);

  // 日历月份范围：拍摄计划起止日期与当前元素受限日期起止的并集；若无则使用今天±3月
  const calendarMonths = useMemo(() => {
    let minStr: string | null = null;
    let maxStr: string | null = null;
    const addDate = (s: string) => {
      const d = s.slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return;
      if (minStr == null || d < minStr) minStr = d;
      if (maxStr == null || d > maxStr) maxStr = d;
    };
    if (resolvedScheduleInfo?.scheduleStartDate) addDate(String(resolvedScheduleInfo.scheduleStartDate));
    if (resolvedScheduleInfo?.scheduleEndDate) addDate(String(resolvedScheduleInfo.scheduleEndDate));
    restrictedDatesSet.forEach(addDate);
    if (minStr != null && maxStr != null) {
      const start = new Date(minStr);
      const end = new Date(maxStr);
      const months: { year: number; month: number }[] = [];
      const y0 = start.getFullYear();
      const m0 = start.getMonth() + 1;
      const y1 = end.getFullYear();
      const m1 = end.getMonth() + 1;
      for (let y = y0; y <= y1; y++) {
        const mStart = y === y0 ? m0 : 1;
        const mEnd = y === y1 ? m1 : 12;
        for (let m = mStart; m <= mEnd; m++) {
          months.push({ year: y, month: m });
        }
      }
      return months;
    }
    // 无任何起止时：今天±3月（与 Schedule 页新建拍摄计划逻辑一致）
    const now = new Date();
    const months: { year: number; month: number }[] = [];
    for (let i = -3; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      months.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
    }
    return months;
  }, [resolvedScheduleInfo?.scheduleStartDate, resolvedScheduleInfo?.scheduleEndDate, restrictedDatesSet]);

  // 文本视图/复制用：开拍/结束日期 + 冲突日期、受限日期、拍摄日期
  const calendarCopyText = useMemo(() => {
    const parts: string[] = [];
    const startStr = resolvedScheduleInfo?.scheduleStartDate ? String(resolvedScheduleInfo.scheduleStartDate).slice(0, 10) : '';
    const endStr = resolvedScheduleInfo?.scheduleEndDate ? String(resolvedScheduleInfo.scheduleEndDate).slice(0, 10) : '';
    if (startStr || endStr) {
      parts.push(`${t('elementManager.startDate')}${t('common.labelSeparator')}${startStr || '—'}`);
      parts.push(`${t('elementManager.endDate')}${t('common.labelSeparator')}${endStr || '—'}`);
      parts.push('');
    }
    const conflictLines = Array.from(conflictDatesSet).sort();
    const restrictedLines = Array.from(restrictedDatesSet).sort();
    const slotShootingDateLines = Array.from(slotDatesSet).sort();
    parts.push(`${t('elementManager.collisionDays')}${t('common.labelSeparator')}\n${conflictLines.join('\n')}`);
    parts.push(`${t('elementManager.externalSchedule')}${t('common.labelSeparator')}\n${restrictedLines.join('\n')}`);
    parts.push(`${t('elementManager.shootingDays')}${t('common.labelSeparator')}\n${slotShootingDateLines.join('\n')}`);
    return parts.join('\n\n');
  }, [resolvedScheduleInfo?.scheduleStartDate, resolvedScheduleInfo?.scheduleEndDate, conflictDatesSet, restrictedDatesSet, slotDatesSet, t]);

  const handleCopyCalendarText = useCallback(() => {
    navigator.clipboard.writeText(calendarCopyText).then(() => {
      setCalendarTextJustCopied(true);
      window.setTimeout(() => setCalendarTextJustCopied(false), 1000);
    }).catch(() => {});
  }, [calendarCopyText]);

  // 日历日期双击：切换受限日期（已存在则删除，否则添加）
  const handleCalendarDateDoubleClick = useCallback(
    (dateStr: string) => {
      if (!selectedElement || !breakdownId) return;
      const current = selectedElement.elementRestrictedDates ?? [];
      const exists = current.some(([d]) => d === dateStr);
      const next: ElementRestrictedDateEntry[] = exists
        ? current.filter(([d]) => d !== dateStr)
        : [...current, [dateStr, ''] as ElementRestrictedDateEntry].sort((a, b) => a[0].localeCompare(b[0]));
      updateElementData(breakdownId, selectedElement.elementID, {
        elementRestrictedDates: next,
      });
      setSelectedElement((prev) =>
        prev ? { ...prev, elementRestrictedDates: next.length > 0 ? next : undefined } : null
      );
      if (isRestrictedDatesEditorOpen) {
        setRestrictedDatesEditorList(next);
        setRestrictedDatesEditorValue(next.map(([d]) => d).join('\n'));
        if (editingNoteDate === dateStr) setEditingNoteDate(null);
      }
    },
    [selectedElement, breakdownId, updateElementData, isRestrictedDatesEditorOpen, editingNoteDate]
  );

  // 判断是否显示简称或最大数量
  const shouldShowAbbr = useMemo(() => {
    if (!selectedElement) return false;
    return selectedElement.elementType === ElementType.CAST;
  }, [selectedElement]);
  
  const shouldShowMaxQty = useMemo(() => {
    if (!selectedElement) return false;
    return [
      ElementType.FEATURED,
      ElementType.BACKGROUND,
      ElementType.STUNT_PLAYERS,
      ElementType.ANIMALS,
      ElementType.VEHICLES
    ].includes(selectedElement.elementType as ElementType);
  }, [selectedElement]);

  
  if (!breakdownId) {
    return (
      <div className="element-manager-container">
        <div className="element-manager-content">
          <p>{t('elementManager.selectBreakdownFirst')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="element-manager-container">
      <div className="element-manager-split-container">
        {/* 左侧：ElementManager-Table */}
        <div className="element-manager-table">
          {/* 1-1、ElementManager-Table-Header（已注释） */}
          {/* <div className="element-manager-table-header"></div> */}
          
          {/* Tabs 和 List 的容器 */}
          <div className="element-manager-table-content">
            <div className="element-manager-tabs">
              {tabs.map(tab => (
                <button
                  key={tab}
                  className={`element-manager-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab(tab);
                  }}
                >
                  {React.createElement(ELEMENT_TYPE_SVG_ICONS[tab], {
                    className: 'element-manager-tab-icon',
                    'aria-label': String(t(`sceneDetails.itemTabs.${tab}`)),
                  })}
                  <span>{String(t(`sceneDetails.itemTabs.${tab}`))}</span>
                </button>
              ))}
            </div>

            {/* 1-3、ElementManager-Table-List */}
            <div className="element-manager-table-list">
              <ElementSearch
                value={null}
                onChange={(element) => {
                  handleElementSelect(element as ElementInfo);
                }}
                visible={true}
                onClose={() => {}}
                breakdownID={breakdownId}
                currentElementType={activeTab}
                fixedColumns={3}
                widthAutoFit={true}
                columnOrderLeftToRight={true}
                useViewIcon={true}
                hideCreateButton={true}
                editable={canEditBreakdown}
                hideShortNameQuantityInput={true}
                selectedElementIds={selectedElement ? [selectedElement.elementID] : []}
                elementIdsWithRestrictedDatesOverlap={elementIdsWithRestrictedDatesOverlap}
              />
            </div>
          </div>
        </div>

        {/* 右侧：ElementManager-Details */}
        <div className="element-manager-details">
          {/* 2-1、ElementManager-Details-Header */}
          <div className="element-manager-details-header">
            <div className="element-manager-details-header-left">
              {selectedElement ? (
                <>
                  <h3 className="element-manager-details-title">
                    {isEditingHeaderName ? (
                      <input
                        ref={headerNameInputRef}
                        type="text"
                        value={localHeaderNameValue}
                        onChange={(e) => setLocalHeaderNameValue(e.target.value)}
                        autoFocus
                        onBlur={handleHeaderNameBlur}
                        onKeyDown={handleHeaderNameKeyDown}
                        className="element-manager-details-title-input"
                      />
                    ) : (
                      <span
                        className="element-manager-details-title-display"
                        onClick={() => {
                          if (!canEditBreakdown) return;
                          setIsEditingHeaderName(true);
                          setLocalHeaderNameValue(selectedElement.elementName || '');
                        }}
                        title={canEditBreakdown ? t('common.clickToEdit') : undefined}
                      >
                        {selectedElement.elementName || ''}
                      </span>
                    )}
                  </h3>
                  {shouldShowAbbr && (
                    <div className="element-manager-details-summary">
                      {t('elementManager.shortName')}{t('common.labelSeparator')}{selectedElement.elementAbbr || '--'}
                    </div>
                  )}
                  {shouldShowMaxQty && (
                    <div className="element-manager-details-summary">
                      {t('elementManager.maxQuantity')}{t('common.labelSeparator')}{selectedElement.elementMaxQtyInBreakdown ?? 1}
                    </div>
                  )}
                </>
              ) : (
                <span className="element-manager-details-header-placeholder">
                  {t('elementManager.selectElementFirst')}
                </span>
              )}
            </div>
            {canEditBreakdown && <div className="element-manager-details-header-actions">
              <button
                ref={changeCategoryButtonRef}
                className="element-manager-action-btn element-manager-change-category-btn"
                tabIndex={-1}
                disabled={!selectedElement}
                onClick={handleChangeCategoryClick}
              >
                <ReplaceIcon className="element-manager-action-icon" aria-label={t('elementManager.changeCategory')} />
                <span>{t('elementManager.changeCategory')}</span>
              </button>
              <button
                type="button"
                className="element-manager-action-btn element-manager-copy-btn"
                tabIndex={-1}
                disabled={!selectedElement || isDuplicatingElement}
                onClick={handleDuplicateElement}
              >
                <CopyIcon className="element-manager-action-icon" aria-label={t('elementManager.duplicateElement')} />
                <span>{t('elementManager.duplicateElement')}</span>
              </button>
              <button
                ref={deleteButtonRef}
                className="element-manager-action-btn element-manager-delete-btn"
                tabIndex={-1}
                disabled={!selectedElement}
                onClick={() => {
                  if (!selectedElement) return;
                  setIsDeleteDialogVisible(true);
                }}
              >
                <DeleteIcon className="element-manager-action-icon" aria-label={t('sceneDetails.delete')} />
                <span>{t('sceneDetails.delete')}</span>
              </button>
            </div>}
          </div>

          {/* 更换分类下拉菜单 */}
          <DropdownMenu
            items={changeCategoryDropdownItems}
            visible={isChangeCategoryMenuOpen}
            onClose={() => setIsChangeCategoryMenuOpen(false)}
            onSelect={handleChangeCategorySelect}
            position={{ top: changeCategoryMenuPosition.top, left: changeCategoryMenuPosition.left }}
            useFixedPosition={true}
            width={200}
            maxHeight={700}
            selectedValue={selectedElement?.elementType ?? null}
            className="element-manager-change-category-dropdown"
          />

          {/* 受限日期编辑弹窗 */}
          {isRestrictedDatesEditorOpen && (
            <div
              ref={restrictedDatesEditorRef}
              className="element-manager-restricted-dates-editor"
              style={{
                position: 'fixed',
                top: restrictedDatesEditorPosition.top,
                left: 'auto',
                right: restrictedDatesEditorPosition.right,
                width: RESTRICTED_DATES_EDITOR_WIDTH,
                zIndex: 1000,
              }}
            >
              <div className="element-manager-restricted-dates-editor-dialog">
                <div className="element-manager-restricted-dates-editor-btn-row">
                  <button
                    type="button"
                    ref={restrictedDatesAddButtonRef}
                    className="element-manager-restricted-dates-editor-btn element-manager-restricted-dates-editor-btn-add"
                    onClick={handleRestrictedDatesAddClick}
                    aria-label={t('elementManager.addDate')}
                  >
                    <AddIcon aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="element-manager-restricted-dates-editor-btn element-manager-restricted-dates-editor-btn-batch"
                    onClick={handleRestrictedDatesOpenBatchEdit}
                  >
                    <BatchEditIcon aria-hidden />
                    <span>{t('elementManager.batchAddOrEditDates')}</span>
                  </button>
                  <button
                    type="button"
                    className="element-manager-restricted-dates-editor-btn-close"
                    onClick={() => handleRestrictedDatesEditorClose()}
                    aria-label={t('sceneDetails.close')}
                  >
                    <CloseIcon aria-hidden />
                  </button>
                </div>
                <div className="element-manager-restricted-dates-editor-body">
                  {isRestrictedDatesBatchEditOpen && (
                    <div className="element-manager-restricted-dates-editor-textarea-wrap">
                      <div className="element-manager-restricted-dates-editor-textarea-inner">
                        <textarea
                          className="element-manager-restricted-dates-editor-textarea"
                          placeholder={t('elementManager.externalSchedulePlaceholder')}
                          value={restrictedDatesEditorValue}
                          onChange={(e) => setRestrictedDatesEditorValue(e.target.value)}
                          rows={Math.min(Math.max(restrictedDatesEditorValue.split('\n').length || 1, 3), 12)}
                        />
                        <div className="element-manager-restricted-dates-editor-textarea-actions">
                          <button
                            type="button"
                            className="element-manager-restricted-dates-editor-btn element-manager-restricted-dates-editor-btn-cancel"
                            onClick={handleRestrictedDatesBatchEditCancel}
                          >
                            {t('common.cancel')}
                          </button>
                          <button
                            type="button"
                            className="element-manager-restricted-dates-editor-btn element-manager-restricted-dates-editor-btn-save"
                            onClick={handleRestrictedDatesBatchEditSave}
                            disabled={hasInvalidRestrictedDatesLines}
                          >
                            {t('common.save')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="element-manager-restricted-dates-editor-list-wrap">
                    <table className="element-manager-restricted-dates-editor-table">
                      <thead>
                        <tr>
                          <th>{t('elementManager.dateColumn')}</th>
                          <th>{t('elementManager.noteColumn')}</th>
                          <th className="element-manager-restricted-dates-editor-cell-delete" aria-hidden />
                        </tr>
                      </thead>
                      <tbody>
                        {restrictedDatesEditorList.map(([date, note]) => (
                          <tr key={date}>
                            <td className="element-manager-restricted-dates-editor-cell-date">{date}</td>
                            <td
                              className="element-manager-restricted-dates-editor-cell-note"
                              onClick={() => setEditingNoteDate(date)}
                            >
                              {editingNoteDate === date ? (
                                <input
                                  type="text"
                                  className="element-manager-restricted-dates-editor-note-input"
                                  value={note}
                                  onChange={(e) => setRestrictedDatesEditorNote(date, e.target.value)}
                                  onBlur={() => setEditingNoteDate(null)}
                                  onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget.blur(), setEditingNoteDate(null))}
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span className="element-manager-restricted-dates-editor-note-text">
                                  {note}
                                </span>
                              )}
                            </td>
                            <td className="element-manager-restricted-dates-editor-cell-delete">
                              <button
                                type="button"
                                className="element-manager-restricted-dates-editor-delete-btn"
                                aria-label={t('sceneDetails.delete')}
                                onClick={() => handleRestrictedDateDelete(date)}
                              >
                                <DeleteIcon className="element-manager-action-icon" aria-hidden />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <span
                  className={`element-manager-restricted-dates-editor-hint ${hasInvalidRestrictedDatesLines ? 'element-manager-restricted-dates-editor-hint-error' : ''}`}
                >
                  {hasInvalidRestrictedDatesLines
                    ? t('elementManager.pleaseFormatCorrectly')
                    : t('elementManager.restrictedDatesEditorHintDefault')}
                </span>
              </div>
            </div>
          )}

          {/* 单日添加 DateSelect 弹窗 */}
          <DateSelect
            visible={isDateSelectVisible}
            onClose={() => setIsDateSelectVisible(false)}
            position={dateSelectPosition}
            selectedYear={new Date().getFullYear()}
            selectedMonth={new Date().getMonth() + 1}
            onSelectMonth={() => {}}
            enableDateSelection
            onSelectDate={handleDateSelectDate}
          />
          
          {/* 删除元素确认对话框 */}
          {selectedElement && deleteButtonRef.current && (
            <ButtonDialog
              visible={isDeleteDialogVisible}
              onClose={() => setIsDeleteDialogVisible(false)}
              onConfirm={async () => {
                if (!breakdownId || !selectedElement) return;

                try {
                  const cache = getElementCache(breakdownId);
                  const itemsOfCurrentType =
                    cache?.elements.filter(
                      (item) => item.elementType === activeTab
                    ) ?? [];

                  const sortedItems =
                    activeTab === ElementType.CAST
                      ? sortCastItemsByNO(itemsOfCurrentType)
                      : sortElements(itemsOfCurrentType);

                  const currentIndex = sortedItems.findIndex(
                    (item: ElementInfo) => item.elementID === selectedElement.elementID
                  );

                  const nextItem =
                    currentIndex >= 0
                      ? sortedItems[currentIndex + 1] ?? sortedItems[currentIndex - 1]
                      : null;

                  await deleteElementData(breakdownId, selectedElement.elementID);

                  if (nextItem) {
                    setSelectedElement(nextItem);
                  } else {
                    setSelectedElement(null);
                  }
                } catch (error) {
                  console.error('[ElementManager] 删除场次元素失败:', error);
                }

                setIsDeleteDialogVisible(false);
              }}
              message={t('elementManager.deleteElementConfirmMessage')}
              confirmText={t('common.confirm')}
              cancelText={t('common.cancel')}
              buttonRef={deleteButtonRef as React.RefObject<HTMLElement>}
              align="right"
            />
          )}
          
          {/* Content Wrapper: 左右分栏布局 */}
          <div className="element-manager-details-content-wrapper">
            {/* 左侧：Info 和 Table */}
            <div className="element-manager-details-left">
              {/* 2-2、ElementManager-Details-Info */}
              <div className="element-manager-details-info">
                {/* 拍摄日期、总场数、受限日期 */}
                <div className="element-manager-detail-item element-manager-detail-item-shooting">
                  <label>{t('elementManager.shootingDays')}</label>
                  <div className="element-manager-detail-value">
                    {selectedElement && effectiveScheduleID && relatedSceneIDs.size > 0 ? (
                      <>
                        {slotDatesSet.size} {t('elementManager.daysUnit')}
                        {slotShootingDateRange && (
                          <>
                            ，{slotShootingDateRange.start} {t('elementManager.dateRangeSeparator')}{' '}
                            {slotShootingDateRange.end}
                          </>
                        )}
                      </>
                    ) : (
                      '--'
                    )}
                  </div>
                </div>
                <div className="element-manager-detail-item element-manager-detail-item-total-scenes">
                  <label>{t('elementManager.totalScenes')}</label>
                  <div className="element-manager-detail-value">
                    {selectedElement
                      ? `${relatedSceneIDs.size} ${t('elementManager.scenesUnit')} / ${relatedScenesTotalPages} ${t('elementManager.pagesUnit')}`
                      : '--'}
                  </div>
                </div>
                <div className="element-manager-detail-item element-manager-detail-item-restricted">
                  <label>{t('elementManager.externalSchedule')}</label>
                  <div
                    ref={restrictedDatesEditButtonRef}
                    className="element-manager-detail-value element-manager-detail-value-with-icon"
                  >
                    <span className="element-manager-detail-value-text">
                      {selectedElement
                        ? `${restrictedDatesSet.size} ${t('elementManager.daysUnit')}`
                        : '--'}
                    </span>
                    {canEditBreakdown && <button
                      type="button"
                      className="element-manager-detail-value-edit-btn"
                      onClick={handleRestrictedDatesEditClick}
                      disabled={!selectedElement}
                      aria-label={t('common.edit')}
                    >
                      <EditIcon className="element-manager-detail-value-edit-icon" />
                    </button>}
                  </div>
                </div>
              </div>

              {/* 2-3、ElementManager-Details-Table-Breakdown */}
              <div className="element-manager-details-table-breakdown">
                {breakdownId && (
                  <ElementSceneList 
                    breakdownId={breakdownId} 
                    selectedElement={selectedElement}
                    canEdit={canEditBreakdown}
                    onEditScene={(sceneId) => setEditingSceneId(sceneId)}
                  />
                )}
              </div>
            </div>
            
            {/* 右侧：日历 */}
            <div className="element-manager-details-right">
              <div className="element-manager-calendar-header">
                <span
                  className={`element-manager-calendar-header-indicator ${conflictDatesSet.size > 0 ? 'has-conflict' : 'no-conflict'}`}
                  aria-hidden
                >
                  {conflictDatesSet.size > 0 ? t('elementManager.hasConflict') : t('elementManager.noConflict')}
                </span>
                <button
                  type="button"
                  className="element-manager-calendar-header-toggle"
                  onClick={() => setShowCalendarTextView((v) => !v)}
                  aria-label={showCalendarTextView ? t('sceneDetails.close') : t('elementManager.duplicateInfoBelow')}
                >
                  {showCalendarTextView ? (
                    <CloseIcon className="element-manager-calendar-header-toggle-icon" />
                  ) : (
                    <TextIcon className="element-manager-calendar-header-toggle-icon" />
                  )}
                </button>
              </div>
              <div className="element-manager-calendar-body">
                {!showCalendarTextView && (
                  <div className="element-manager-calendar">
                    <div className="element-manager-calendar-weekdays">
                      {['日', '一', '二', '三', '四', '五', '六'].map((d) => (
                        <div key={d} className="element-manager-calendar-weekday">{d}</div>
                      ))}
                    </div>
                    <div className="element-manager-calendar-months">
                      {calendarMonths.map(({ year, month }, monthIndex) => {
                        const firstDay = new Date(year, month - 1, 1);
                        const daysInMonth = new Date(year, month, 0).getDate();
                        const startDayOfWeek = firstDay.getDay();
                        const totalCells = Math.ceil((startDayOfWeek + daysInMonth) / 7) * 7;
                        const leadingEmpty = startDayOfWeek;
                        const dateCells: (number | null)[] = [
                          ...Array(leadingEmpty).fill(null),
                          ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
                          ...Array(totalCells - leadingEmpty - daysInMonth).fill(null),
                        ];
                        const pad = (n: number) => String(n).padStart(2, '0');
                        const getDateType = (d: number | null): 'calDate' | 'restrictedDates' | 'conflictDates' | null => {
                          if (d === null) return null;
                          const dateStr = `${year}-${pad(month)}-${pad(d)}`;
                          const inSlotShootingDates = slotDatesSet.has(dateStr);
                          const inRestricted = restrictedDatesSet.has(dateStr);
                          if (inSlotShootingDates && inRestricted) return 'conflictDates';
                          if (inSlotShootingDates) return 'calDate';
                          if (inRestricted) return 'restrictedDates';
                          return null;
                        };
                        return (
                          <div key={`${year}-${month}`} className="element-manager-calendar-month">
                            <div
                              className={`element-manager-calendar-month-title ${monthIndex === 0 || month === 1 ? 'element-manager-calendar-month-title-with-year' : ''}`}
                            >
                              {monthIndex === 0 || month === 1 ? (
                                <>
                                  <span>{month} 月</span>
                                  <span className="element-manager-calendar-month-title-year">{year} 年</span>
                                </>
                              ) : (
                                `${month}月`
                              )}
                            </div>
                            <div className="element-manager-calendar-dates">
                              {dateCells.map((d, i) => {
                                const dateType = getDateType(d);
                                const dateStr = d !== null ? `${year}-${pad(month)}-${pad(d)}` : null;
                                return (
                                  <div
                                    key={i}
                                    className={`element-manager-calendar-date-cell ${d === null ? 'empty' : ''} ${dateType ? `element-manager-calendar-date-${dateType}` : ''} ${canEditBreakdown ? '' : 'element-manager-calendar-date-cell-readonly'}`}
                                    onClick={canEditBreakdown ? undefined : (event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                    }}
                                    onDoubleClick={canEditBreakdown && dateStr && selectedElement ? () => handleCalendarDateDoubleClick(dateStr) : undefined}
                                    title={
                                      canEditBreakdown && dateStr && selectedElement
                                        ? restrictedDatesSet.has(dateStr)
                                          ? t('elementManager.doubleClickToRemoveDate')
                                          : t('elementManager.doubleClickToAddDate')
                                        : undefined
                                    }
                                  >
                                    {d ?? ''}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {showCalendarTextView && (
                  <div className="element-manager-calendar-text">
                    <button
                      type="button"
                      className="element-manager-calendar-text-copy-btn"
                      onClick={handleCopyCalendarText}
                    >
                      <CopyIcon aria-hidden />
                      {calendarTextJustCopied ? t('elementManager.duplicated') : t('elementManager.duplicateInfoBelow')}
                    </button>
                    <div className="element-manager-calendar-text-content">
                      {(resolvedScheduleInfo?.scheduleStartDate || resolvedScheduleInfo?.scheduleEndDate) && (
                        <div className="element-manager-calendar-text-section element-manager-calendar-text-slot-dates">
                          <div className="element-manager-calendar-text-section-title">
                            {t('elementManager.startDate')}{t('common.labelSeparator')}{resolvedScheduleInfo?.scheduleStartDate ? String(resolvedScheduleInfo.scheduleStartDate).slice(0, 10) : '—'}
                          </div>
                          <div className="element-manager-calendar-text-section-title">
                            {t('elementManager.endDate')}{t('common.labelSeparator')}{resolvedScheduleInfo?.scheduleEndDate ? String(resolvedScheduleInfo.scheduleEndDate).slice(0, 10) : '—'}
                          </div>
                        </div>
                      )}
                      <div className="element-manager-calendar-text-section">
                        <div className="element-manager-calendar-text-section-title">
                          {t('elementManager.collisionDays')}{t('common.labelSeparator')}
                        </div>
                        <div className="element-manager-calendar-text-section-dates">
                          {Array.from(conflictDatesSet).sort().join('\n') || '—'}
                        </div>
                      </div>
                      <div className="element-manager-calendar-text-section">
                        <div className="element-manager-calendar-text-section-title">
                          {t('elementManager.externalSchedule')}{t('common.labelSeparator')}
                        </div>
                        <div className="element-manager-calendar-text-section-dates">
                          {Array.from(restrictedDatesSet).sort().join('\n') || '—'}
                        </div>
                      </div>
                      <div className="element-manager-calendar-text-section">
                        <div className="element-manager-calendar-text-section-title">
                          {t('elementManager.shootingDays')}{t('common.labelSeparator')}
                        </div>
                        <div className="element-manager-calendar-text-section-dates">
                          {Array.from(slotDatesSet).sort().join('\n') || '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 场次详情抽屉：从整页最右侧打开，支持分类 tabs 调起 ElementSearch */}
      {editingSceneId && breakdownId && (
        <EditScene
          visible={!!editingSceneId}
          onClose={() => setEditingSceneId(null)}
          sceneId={editingSceneId}
          breakdownID={breakdownId}
          canEdit={canEditBreakdown}
          drawerPosition="rightEdge"
          drawerTop={EDIT_SCENE_PANEL_DRAWER_TOP_PX}
        />
      )}
    </div>
  );
};

export default ElementManager;
